import {
    CroppedRegion,
    ImageSize,
    LookitItemCategoryId,
    SearchTargetRegionSource,
    SelectedRegionCrop,
    SelectedRegionCropBasis,
    UploadImageDeleteApiRequest,
    UploadImageDeleteArtifactResult
} from './domain';
import {
    ResolvedSelectedRegionCrop,
    resolveSelectedRegionCrop
} from './crop-utils';

export interface CreateCropArtifactApiRequest {
    selectedRegion: CroppedRegion;
    displayedImageSize: ImageSize;
    originalImageSize: ImageSize;
    normalizedImageId: string;
    selectedCategoryId: LookitItemCategoryId | string;
    regionSource: SearchTargetRegionSource;
}

export interface CreateCropArtifactInput extends CreateCropArtifactApiRequest {
    uploadImageId: string;
    previewUrl?: string | null;
    localPreviewId?: string | null;
    cropBasis?: SelectedRegionCropBasis | null;
    mimeType?: string | null;
    retentionSeconds?: number;
    expiresAt?: string | null;
    now?: Date;
}

export interface CropArtifactCoordinateBasis {
    selectedRegion: 'display_percent_after_orientation';
    cropRegionOriginal: 'normalized_pixels_after_orientation';
    originalImageSize: 'normalized_image_pixels';
}

export interface CropArtifactMetadata {
    cropImageId: string;
    uploadImageId: string;
    normalizedImageId: string;
    selectedCategoryId: LookitItemCategoryId | string;
    regionSource: SearchTargetRegionSource;
    selectedRegion: CroppedRegion;
    displayedImageSize: ImageSize;
    originalImageSize: ImageSize;
    cropRegionOriginal: CroppedRegion;
    cropWidth: number;
    cropHeight: number;
    previewUrl: string | null;
    localPreviewId: string | null;
    expiresAt: string;
    createdAt: string;
    cropBasis: SelectedRegionCropBasis;
    coordinateBasis: CropArtifactCoordinateBasis;
    clippedToImageBounds: boolean;
    isFullImage: boolean;
    selectedCrop: SelectedRegionCrop;
}

export interface CreateCropArtifactApiResponse extends CropArtifactMetadata {
    ok: true;
}

export interface CropArtifactRepository {
    dataKind: 'crop_image';
    repository: 'crop_artifact_repository';
    createCropArtifact(input: CreateCropArtifactInput): Promise<CreateCropArtifactApiResponse>;
    getCropArtifact(cropImageId: string): Promise<CropArtifactMetadata | null>;
    listByUploadImageId(uploadImageId: string): Promise<CropArtifactMetadata[]>;
    deleteByUploadImageId(input: {
        uploadImageId: string;
        requestedAt: string;
        reason: UploadImageDeleteApiRequest['reason'];
    }): Promise<UploadImageDeleteArtifactResult>;
}

export interface CropArtifactHttpResponse {
    ok: boolean;
    status: number;
    json?: () => Promise<unknown>;
    text?: () => Promise<string>;
}

export type CropArtifactFetch = (input: string, init: {
    method: string;
    headers: Record<string, string>;
    body: string;
}) => Promise<CropArtifactHttpResponse>;

export interface CropArtifactApiServiceOptions {
    endpointBase?: string;
    fetchImpl?: CropArtifactFetch;
}

export interface CropArtifactService {
    createCropArtifact(input: CreateCropArtifactInput): Promise<CreateCropArtifactApiResponse>;
}

export const defaultCropArtifactRetentionSeconds = 7 * 24 * 60 * 60;
export const defaultCropArtifactApiEndpointBase = '/api/lookit/uploads';

let cropSequence = 0;

function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

function addSeconds(date: Date, seconds: number): Date {
    return new Date(date.getTime() + seconds * 1000);
}

function nextCropImageId(now: Date): string {
    cropSequence += 1;
    return `crop-memory-${now.getTime()}-${String(cropSequence).padStart(4, '0')}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringField(record: Record<string, unknown>, key: string): string | null {
    const value = record[key];
    return typeof value === 'string' && value.trim() ? value : null;
}

function numberField(record: Record<string, unknown>, key: string): number | null {
    const value = record[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function booleanField(record: Record<string, unknown>, key: string): boolean | null {
    const value = record[key];
    return typeof value === 'boolean' ? value : null;
}

function recordPayload(payload: unknown): Record<string, unknown> | null {
    if (!isRecord(payload)) return null;
    if (isRecord(payload.data)) {
        if (typeof payload.data.cropImageId === 'string') return payload.data;
        if (isRecord(payload.data.crop)) return payload.data.crop;
    }
    if (typeof payload.cropImageId === 'string') return payload;
    if (isRecord(payload.crop)) return payload.crop;
    return null;
}

function regionField(record: Record<string, unknown>, key: string): CroppedRegion | null {
    const value = record[key];
    if (!isRecord(value)) return null;

    const x = numberField(value, 'x');
    const y = numberField(value, 'y');
    const width = numberField(value, 'width');
    const height = numberField(value, 'height');
    if (x === null || y === null || width === null || height === null) return null;

    return { x, y, width, height };
}

function imageSizeField(record: Record<string, unknown>, key: string): ImageSize | null {
    const value = record[key];
    if (!isRecord(value)) return null;

    const width = numberField(value, 'width');
    const height = numberField(value, 'height');
    if (width === null || height === null) return null;

    return { width, height };
}

function normalizeCropArtifactResponse(payload: unknown): CreateCropArtifactApiResponse | null {
    const record = recordPayload(payload);
    if (!record) return null;

    const cropImageId = stringField(record, 'cropImageId') || stringField(record, 'crop_image_id');
    const uploadImageId = stringField(record, 'uploadImageId') || stringField(record, 'upload_image_id');
    const normalizedImageId = stringField(record, 'normalizedImageId') || stringField(record, 'normalized_image_id');
    const selectedCategoryId = stringField(record, 'selectedCategoryId') || stringField(record, 'selected_category_id');
    const regionSource = stringField(record, 'regionSource') || stringField(record, 'region_source');
    const selectedRegion = regionField(record, 'selectedRegion') || regionField(record, 'selected_region');
    const displayedImageSize = imageSizeField(record, 'displayedImageSize') || imageSizeField(record, 'displayed_image_size');
    const originalImageSize = imageSizeField(record, 'originalImageSize') || imageSizeField(record, 'original_image_size');
    const cropRegionOriginal = regionField(record, 'cropRegionOriginal') || regionField(record, 'crop_region_original');
    const cropWidth = numberField(record, 'cropWidth') ?? numberField(record, 'crop_width');
    const cropHeight = numberField(record, 'cropHeight') ?? numberField(record, 'crop_height');
    const expiresAt = stringField(record, 'expiresAt') || stringField(record, 'expires_at');
    const createdAt = stringField(record, 'createdAt') || stringField(record, 'created_at') || new Date().toISOString();
    const cropBasis = (stringField(record, 'cropBasis') || stringField(record, 'crop_basis') || 'user_adjusted_region') as SelectedRegionCropBasis;

    if (
        !cropImageId ||
        !uploadImageId ||
        !normalizedImageId ||
        !selectedCategoryId ||
        !regionSource ||
        !selectedRegion ||
        !displayedImageSize ||
        !originalImageSize ||
        !cropRegionOriginal ||
        cropWidth === null ||
        cropHeight === null ||
        !expiresAt
    ) {
        return null;
    }

    const previewUrl = stringField(record, 'previewUrl') || stringField(record, 'preview_url');
    const localPreviewId = stringField(record, 'localPreviewId') || stringField(record, 'local_preview_id');
    const selectedCropRecord = isRecord(record.selectedCrop) ? record.selectedCrop : isRecord(record.selected_crop) ? record.selected_crop : null;
    const selectedCrop: SelectedRegionCrop = selectedCropRecord
        ? selectedCropRecord as SelectedRegionCrop
        : {
            crop_image_id: cropImageId,
            crop_url: null,
            crop_preview_url: previewUrl,
            local_crop_id: localPreviewId,
            selected_region: selectedRegion,
            original_image_size: originalImageSize,
            displayed_image_size: displayedImageSize,
            crop_region_original: cropRegionOriginal,
            mime_type: 'image/webp',
            width: cropWidth,
            height: cropHeight,
            expires_at: expiresAt,
            crop_basis: cropBasis,
            background_removed: false,
            source_mask_url: null
        };

    return {
        ok: true,
        cropImageId,
        uploadImageId,
        normalizedImageId,
        selectedCategoryId,
        regionSource: regionSource as SearchTargetRegionSource,
        selectedRegion,
        displayedImageSize,
        originalImageSize,
        cropRegionOriginal,
        cropWidth,
        cropHeight,
        previewUrl,
        localPreviewId,
        expiresAt,
        createdAt,
        cropBasis,
        coordinateBasis: isRecord(record.coordinateBasis)
            ? record.coordinateBasis as unknown as CropArtifactCoordinateBasis
            : {
                selectedRegion: 'display_percent_after_orientation',
                cropRegionOriginal: 'normalized_pixels_after_orientation',
                originalImageSize: 'normalized_image_pixels'
            },
        clippedToImageBounds: booleanField(record, 'clippedToImageBounds') ?? booleanField(record, 'clipped_to_image_bounds') ?? false,
        isFullImage: booleanField(record, 'isFullImage') ?? booleanField(record, 'is_full_image') ?? false,
        selectedCrop: {
            ...selectedCrop,
            crop_image_id: selectedCrop.crop_image_id || cropImageId,
            crop_region_original: selectedCrop.crop_region_original || cropRegionOriginal,
            selected_region: selectedCrop.selected_region || selectedRegion,
            original_image_size: selectedCrop.original_image_size || originalImageSize,
            displayed_image_size: selectedCrop.displayed_image_size || displayedImageSize,
            width: selectedCrop.width || cropWidth,
            height: selectedCrop.height || cropHeight,
            expires_at: selectedCrop.expires_at || expiresAt,
            crop_basis: selectedCrop.crop_basis || cropBasis
        }
    };
}

function setJsonParam(params: URLSearchParams, key: string, value: unknown) {
    if (value === undefined || value === null) return;
    params.set(key, JSON.stringify(value));
}

export function createCropArtifactApiRequestBody(input: CreateCropArtifactInput): URLSearchParams {
    const params = new URLSearchParams();
    setJsonParam(params, 'selectedRegion', input.selectedRegion);
    setJsonParam(params, 'displayedImageSize', input.displayedImageSize);
    setJsonParam(params, 'originalImageSize', input.originalImageSize);
    params.set('normalizedImageId', input.normalizedImageId);
    params.set('selectedCategoryId', input.selectedCategoryId);
    params.set('regionSource', input.regionSource);
    if (input.localPreviewId) params.set('localPreviewId', input.localPreviewId);
    if (input.cropBasis) params.set('cropBasis', input.cropBasis);
    return params;
}

async function readJsonPayload(response: CropArtifactHttpResponse): Promise<unknown> {
    if (typeof response.json === 'function') return response.json();
    if (typeof response.text === 'function') {
        const text = await response.text();
        return text ? JSON.parse(text) : null;
    }
    return null;
}

function runtimeFetch(): CropArtifactFetch | null {
    const scope = typeof globalThis === 'undefined' ? null : globalThis as unknown as { fetch?: unknown };
    return typeof scope?.fetch === 'function' ? scope.fetch as CropArtifactFetch : null;
}

export function createApiCropArtifactService(options: CropArtifactApiServiceOptions = {}): CropArtifactService {
    const endpointBase = (options.endpointBase || defaultCropArtifactApiEndpointBase).replace(/\/$/, '');
    const fetchImpl = options.fetchImpl || runtimeFetch();

    return {
        async createCropArtifact(input: CreateCropArtifactInput): Promise<CreateCropArtifactApiResponse> {
            if (!fetchImpl) {
                throw new Error('crop artifact API fetch is unavailable');
            }

            const response = await fetchImpl(`${endpointBase}/${encodeURIComponent(input.uploadImageId)}/crops`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: createCropArtifactApiRequestBody(input).toString()
            });
            const payload = await readJsonPayload(response);
            const crop = normalizeCropArtifactResponse(payload);
            if (!crop) {
                throw new Error(`crop artifact API response could not be parsed (${response.status})`);
            }
            return crop;
        }
    };
}

function artifactResult(input: {
    status: UploadImageDeleteArtifactResult['status'];
    operation: UploadImageDeleteArtifactResult['operation'];
    deletedAt?: string | null;
    message?: string | null;
}): UploadImageDeleteArtifactResult {
    return {
        data_kind: 'crop_image',
        status: input.status,
        repository: 'crop_artifact_repository',
        operation: input.operation,
        deleted_at: input.deletedAt ?? null,
        message: input.message ?? null
    };
}

function selectedCropFromArtifact(input: {
    cropImageId: string;
    metadata: Omit<CropArtifactMetadata, 'selectedCrop'>;
    mimeType?: string | null;
}): SelectedRegionCrop {
    return {
        crop_image_id: input.cropImageId,
        crop_url: null,
        crop_preview_url: input.metadata.previewUrl,
        local_crop_id: input.metadata.localPreviewId,
        selected_region: cloneData(input.metadata.selectedRegion),
        original_image_size: cloneData(input.metadata.originalImageSize),
        displayed_image_size: cloneData(input.metadata.displayedImageSize),
        crop_region_original: cloneData(input.metadata.cropRegionOriginal),
        mime_type: input.mimeType || 'image/webp',
        width: input.metadata.cropWidth,
        height: input.metadata.cropHeight,
        expires_at: input.metadata.expiresAt,
        crop_basis: input.metadata.cropBasis,
        background_removed: false,
        source_mask_url: null
    };
}

function cropMetadataFromResolved(
    input: CreateCropArtifactInput,
    resolved: ResolvedSelectedRegionCrop
): CropArtifactMetadata {
    const now = input.now || new Date();
    const cropImageId = nextCropImageId(now);
    const expiresAt = input.expiresAt || addSeconds(now, input.retentionSeconds ?? defaultCropArtifactRetentionSeconds).toISOString();
    const cropBasis = input.cropBasis || (input.regionSource === 'full_image' ? 'full_image' : 'user_adjusted_region');
    const baseMetadata: Omit<CropArtifactMetadata, 'selectedCrop'> = {
        cropImageId,
        uploadImageId: input.uploadImageId,
        normalizedImageId: input.normalizedImageId,
        selectedCategoryId: input.selectedCategoryId,
        regionSource: input.regionSource,
        selectedRegion: cloneData(resolved.selectedRegion),
        displayedImageSize: cloneData(input.displayedImageSize),
        originalImageSize: cloneData(input.originalImageSize),
        cropRegionOriginal: cloneData(resolved.cropRegionOriginal),
        cropWidth: resolved.cropWidth,
        cropHeight: resolved.cropHeight,
        previewUrl: input.previewUrl || null,
        localPreviewId: input.localPreviewId || `mock-preview-${cropImageId}`,
        expiresAt,
        createdAt: now.toISOString(),
        cropBasis,
        coordinateBasis: {
            selectedRegion: 'display_percent_after_orientation',
            cropRegionOriginal: 'normalized_pixels_after_orientation',
            originalImageSize: 'normalized_image_pixels'
        },
        clippedToImageBounds: resolved.clippedToImageBounds,
        isFullImage: resolved.isFullImage
    };

    return {
        ...baseMetadata,
        selectedCrop: selectedCropFromArtifact({
            cropImageId,
            metadata: baseMetadata,
            mimeType: input.mimeType
        })
    };
}

export class InMemoryCropArtifactRepository implements CropArtifactRepository {
    public readonly dataKind = 'crop_image' as const;
    public readonly repository = 'crop_artifact_repository' as const;
    private readonly records = new Map<string, CropArtifactMetadata>();

    async createCropArtifact(input: CreateCropArtifactInput): Promise<CreateCropArtifactApiResponse> {
        if (!input.uploadImageId.trim()) throw new Error('uploadImageId is required');
        if (!input.normalizedImageId.trim()) throw new Error('normalizedImageId is required');
        if (!input.selectedCategoryId) throw new Error('selectedCategoryId is required');
        if (!input.regionSource) throw new Error('regionSource is required');

        const resolved = resolveSelectedRegionCrop({
            selectedRegion: input.selectedRegion,
            displayedImageSize: input.displayedImageSize,
            originalImageSize: input.originalImageSize
        });
        const metadata = cropMetadataFromResolved(input, resolved);
        this.records.set(metadata.cropImageId, metadata);

        return {
            ok: true,
            ...cloneData(metadata)
        };
    }

    async getCropArtifact(cropImageId: string): Promise<CropArtifactMetadata | null> {
        const record = this.records.get(cropImageId);
        return record ? cloneData(record) : null;
    }

    async listByUploadImageId(uploadImageId: string): Promise<CropArtifactMetadata[]> {
        return Array.from(this.records.values())
            .filter((record) => record.uploadImageId === uploadImageId)
            .map((record) => cloneData(record));
    }

    async deleteByUploadImageId(input: {
        uploadImageId: string;
        requestedAt: string;
        reason: UploadImageDeleteApiRequest['reason'];
    }): Promise<UploadImageDeleteArtifactResult> {
        const cropIds = Array.from(this.records.values())
            .filter((record) => record.uploadImageId === input.uploadImageId)
            .map((record) => record.cropImageId);

        cropIds.forEach((cropImageId) => this.records.delete(cropImageId));

        return cropIds.length > 0
            ? artifactResult({
                status: 'deleted',
                operation: 'delete',
                deletedAt: input.requestedAt,
                message: 'crop artifact metadata deleted'
            })
            : artifactResult({
                status: 'no_records',
                operation: 'noop_delete',
                deletedAt: input.requestedAt,
                message: '삭제할 crop artifact metadata가 없습니다.'
            });
    }
}

export const inMemoryCropArtifactRepository = new InMemoryCropArtifactRepository();

export const cropArtifactService = {
    createApiCropArtifactService,
    createCropArtifactApiRequestBody
};
